import React from "react";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card, CardHeader } from "../../../components";
import type { DailyDispatchPoint } from "../domain/types";

interface Props {
  data: DailyDispatchPoint[];
}

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function dayToMonth(day: number): string {
  let cum = 0;
  for (let i = 0; i < 12; i++) {
    cum += MONTH_DAYS[i];
    if (day <= cum) return MONTH_LABELS[i];
  }
  return "Dec";
}

function monthTicks(): number[] {
  const ticks: number[] = [];
  let cum = 0;
  for (const d of MONTH_DAYS) {
    ticks.push(cum + 1);
    cum += d;
  }
  return ticks;
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-body-sm shadow-lg"
      style={{
        backgroundColor: "#1e293b",
        border: "1px solid #334155",
      }}
    >
      <div className="text-text-muted mb-1">Day {label} ({dayToMonth(label)})</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="text-text-primary font-medium">
            {p.value.toFixed(1)} MW avg
          </span>
        </div>
      ))}
    </div>
  );
}

function CustomLegend({ payload }: any) {
  if (!payload) return null;
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-2 text-body-sm">
      {payload.map((entry: any) => (
        <div key={entry.value} className="flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-text-muted">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

const AREAS = [
  { key: "re_direct", name: "RE Direct", color: "#f59e0b" },
  { key: "bess", name: "BESS", color: "#8b5cf6" },
  { key: "ng", name: "Natural Gas", color: "#ef4444" },
  { key: "curtailed", name: "Curtailed", color: "#6b7280" },
  { key: "unserved", name: "Unserved", color: "#f97316" },
];

export function LoadGenerationChart({ data }: Props) {
  if (!data || data.length === 0) return null;

  return (
    <Card>
      <CardHeader>Annual Generation Profile</CardHeader>
      <div className="p-4">
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <defs>
              {AREAS.map(({ key, color }) => (
                <linearGradient key={key} id={`grad_${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="day"
              ticks={monthTicks()}
              tickFormatter={(d) => dayToMonth(d)}
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
              label={{
                value: "MW (24hr Avg)",
                angle: -90,
                position: "insideLeft",
                style: { fill: "#e2e8f0", fontSize: 14, fontWeight: 600 },
              }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />

            {AREAS.map(({ key, name, color }) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                name={name}
                stackId="1"
                stroke={color}
                fill={`url(#grad_${key})`}
                strokeWidth={0}
              />
            ))}

            <Line
              type="monotone"
              dataKey="load"
              name="Load"
              stroke="#ffffff"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
